package com.essntl.features.authentication.ui.auth.components

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.focus.focusProperties
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.LinkAnnotation
import androidx.compose.ui.text.SpanStyle
import androidx.compose.ui.text.TextLinkStyles
import androidx.compose.ui.text.buildAnnotatedString
import androidx.compose.ui.text.withLink
import com.essntl.features.authentication.domain.models.CheckModel
import com.essntl.features.authentication.ui.auth.AuthEvent
import com.essntl.features.authentication.ui.auth.AuthState
import com.essntl.features.authentication.ui.auth.AuthValidation
import com.essntl.uikit.components.button.primary.EsMediumPrimaryButton
import com.essntl.uikit.components.checkbox.EsPrimaryCheckBox
import com.essntl.uikit.components.input.EsInput
import com.essntl.uikit.components.utils.EsConstants
import com.essntl.uikit.components.utils.spacing
import com.essntl.uikit.theme.esColorScheme
import com.essntl.uikit.theme.esTypography
import core.validation.EsInputState

@Composable
fun AuthCard(
    state: AuthState,
    validation: AuthValidation,
    onEvent: (AuthEvent) -> Unit,
    modifier: Modifier = Modifier,
) {
    val isLoading = remember(state) { state.isLoading }

    var isChecked by remember { mutableStateOf(false) }

    Column(
        verticalArrangement = Arrangement.spacedBy(1.5.spacing),
        modifier = modifier
            .clip(RoundedCornerShape(EsConstants.SectionBorderRadius))
            .background(MaterialTheme.esColorScheme.bgSecondary)
            .padding(1.5.spacing),
    ) {
        Column {
            Text(
                text = when (state.checkSubmit.data?.state) {
                    null, CheckModel.State.NO_TENANT_FOUND -> "Let's get started"
                    CheckModel.State.USER_NOT_FOUND -> "Register your account"
                    CheckModel.State.OTP_SENT -> "You've got mail"
                },
                color = MaterialTheme.esColorScheme.textPrimary,
                style = MaterialTheme.esTypography.h2Bold32,
            )

            Spacer(modifier = Modifier.height(.5.spacing))

            Text(
                text = when (state.checkSubmit.data?.state) {
                    null, CheckModel.State.NO_TENANT_FOUND -> "It’s great to have you with us. Enter your email to get started."
                    CheckModel.State.USER_NOT_FOUND -> "To register your account please add your first and last name"
                    CheckModel.State.OTP_SENT -> "You have been sent an OTP Please check your email and the code below"
                },
                color = MaterialTheme.esColorScheme.textSecondary,
                style = MaterialTheme.esTypography.p1Regular16,
            )
        }

        when (state.checkSubmit.data?.state) {
            null, CheckModel.State.NO_TENANT_FOUND -> {
                EsInput(
                    value = state.email,
                    state = if (state.emailError.isNotEmpty())
                        EsInputState.Error else
                        validation.email.state,
                    message = state.emailError.ifEmpty { "This field is required" },
                    onValueChange = {
                        validation.email.validate(it)
                        onEvent(
                            AuthEvent.UpdateAuthFields(
                                state.copy(
                                    email = it,
                                ),
                            ),
                        )
                    },
                    placeholder = "Enter email",
                )
            }

            CheckModel.State.USER_NOT_FOUND -> {
                Column {
                    EsInput(
                        value = state.firstName,
                        state = validation.registerForm.firstName.state,
                        message = "This field is required",
                        onValueChange = {
                            validation.registerForm.firstName.validate(it)
                            onEvent(
                                AuthEvent.UpdateAuthFields(
                                    state.copy(firstName = it),
                                ),
                            )
                        },
                        label = "First name",
                        placeholder = "Enter First name",
                    )

                    Spacer(modifier = Modifier.height(.5.spacing))

                    EsInput(
                        value = state.lastName,
                        state = validation.registerForm.lastName.state,
                        message = "This field is required",
                        onValueChange = {
                            validation.registerForm.lastName.validate(it)
                            onEvent(
                                AuthEvent.UpdateAuthFields(
                                    state.copy(lastName = it),
                                ),
                            )
                        },
                        label = "Last name",
                        placeholder = "Enter last name",
                    )

                    Spacer(modifier = Modifier.height(1.spacing))

                    Row(
                        horizontalArrangement = Arrangement.Center,
                        verticalAlignment = Alignment.CenterVertically,
                    ) {
                        EsPrimaryCheckBox(
                            checked = isChecked,
                            onCheckedChange = { isChecked = it },
                            modifier = Modifier
                                .focusProperties { canFocus = false }
                                .align(Alignment.CenterVertically),
                        )

                        Spacer(modifier = Modifier.width(1.spacing))

                        Text(
                            text = buildAnnotatedString {
                                append("By continuing, you agree the ")

                                withLink(
                                    LinkAnnotation.Url(
                                        url = "https://www.odia.app/terms",
                                        styles = TextLinkStyles(
                                            style = SpanStyle(
                                                color = Color.Blue,
                                            ),
                                        ),
                                    ),
                                ) {
                                    append("Terms of Service")
                                }

                                append(" and ")

                                withLink(
                                    LinkAnnotation.Url(
                                        url = "https://www.odia.app/privacy",
                                        styles = TextLinkStyles(
                                            style = SpanStyle(
                                                color = Color.Blue,
                                            ),
                                        ),
                                    ),
                                ) {
                                    append("Privacy Policy")
                                }

                                append(" of Odia. Please check the box to proceed.")
                            },
                            style = MaterialTheme.esTypography.c1Medium11,
                        )
                    }
                }
            }

            CheckModel.State.OTP_SENT -> {
                EsInput(
                    value = state.otp,
                    state = validation.loginForm.otp.state,
                    message = "This field is required",
                    onValueChange = {
                        validation.loginForm.otp.validate(it)
                        onEvent(
                            AuthEvent.UpdateAuthFields(
                                state.copy(otp = it),
                            ),
                        )
                    },
                    label = "Otp",
                    placeholder = "Enter Otp",
                )
            }
        }

        Row(
            horizontalArrangement = Arrangement.spacedBy(.5.spacing),
            modifier = Modifier.fillMaxWidth(),
        ) {
            if (state.checkSubmit.data?.state != null &&
                state.checkSubmit.data?.state != CheckModel.State.NO_TENANT_FOUND
            )
                EsMediumPrimaryButton(
                    text = "Back",
                    modifier = Modifier.weight(1f),
                    onClick = {
                        onEvent(AuthEvent.Back)
                    },
                )

            EsMediumPrimaryButton(
                onClick = {
                    when (state.checkSubmit.data?.state) {
                        null, CheckModel.State.NO_TENANT_FOUND -> {
                            if (validation.email.validate())
                                onEvent(AuthEvent.CheckEmail)
                        }

                        CheckModel.State.USER_NOT_FOUND -> {
                            if (validation.registerForm.validate())
                                onEvent(AuthEvent.Register)
                        }

                        CheckModel.State.OTP_SENT -> {
                            if (validation.loginForm.validate())
                                onEvent(AuthEvent.Login)
                        }
                    }
                },
                loading = isLoading,
                text = when (state.checkSubmit.data?.state) {
                    null, CheckModel.State.NO_TENANT_FOUND -> "Confirm Email"
                    CheckModel.State.USER_NOT_FOUND -> "Continue"
                    CheckModel.State.OTP_SENT -> "Login"
                },
                enabled = when (state.checkSubmit.data?.state) {
                    null, CheckModel.State.NO_TENANT_FOUND -> validation.email.value.isNotEmpty()
                    CheckModel.State.USER_NOT_FOUND -> validation.registerForm.isValid && isChecked
                    CheckModel.State.OTP_SENT -> validation.loginForm.isValid
                },
                modifier = Modifier.weight(1f),
            )
        }
    }
}